import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import {
  IcDeleteIcon,
  IcLogOutIcon,
  IcMarkAsReadIcon,
  IcPinStrokeIcon,
  IcSUnpinIcon,
} from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IPopoverMenuItem } from '@gem/ui-kit';

import { useSpaceActions } from 'APP/main/hooks/space/useSpaceActions';
import { SpaceTemplate } from 'APP/model/space/spaceModel.types';
import Entities from 'STORE';
import { Space } from 'STORE/Spaces/Space';

interface ISpaceListItemPresenter {
  isShowOnboarding: boolean;
  hasContextMenu: boolean;
  menuItems: IPopoverMenuItem[];
}

export function useSpaceListItemPresenter(space: Space): ISpaceListItemPresenter {
  const { t } = useTranslation();
  const source = useAsObservableSource({ space });
  const spaceActions = useSpaceActions(space);

  const presenter = useLocalStore<ISpaceListItemPresenter>(() => ({
    get isShowOnboarding(): boolean {
      return (
        !Entities.GroupsStore.isLoading &&
        !Entities.spacesStore.isLoading &&
        source.space.template === SpaceTemplate.Friends &&
        source.space.isOwner
      );
    },

    get hasContextMenu(): boolean {
      return !!presenter.menuItems.length;
    },

    get menuItems(): IPopoverMenuItem[] {
      return [
        {
          key: 'markAsRead',
          title: t('action_over_chat_mark_as_read'),
          icon: IcMarkAsReadIcon,
          isVisible: spaceActions.canMarkAsRead,
          onClick: spaceActions.onMarkAsRead,
        },
        {
          key: 'pin',
          title: t('common_pin'),
          icon: IcPinStrokeIcon,
          isVisible: spaceActions.canPin,
          onClick: spaceActions.onPin,
        },
        {
          key: 'unpin',
          title: t('common_unpin'),
          icon: IcSUnpinIcon,
          isVisible: spaceActions.canUnpin,
          onClick: spaceActions.onUnpin,
        },
        {
          key: 'leave',
          title: t('space_leave_space'),
          icon: IcLogOutIcon,
          isVisible: spaceActions.canLeave,
          onClick: spaceActions.onLeave,
        },
        {
          key: 'delete',
          title: t('space_delete_space'),
          icon: IcDeleteIcon,
          isVisible: spaceActions.canDelete,
          onClick: spaceActions.onDelete,
        },
      ].filter((item) => item.isVisible);
    },
  }));

  return presenter;
}
